import React, { useState, useEffect } from 'react';
import '../../App.css';
import {
  TextField,
  Box,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';

const initialFValues = {
  id: 0,
  ed_type: '',
  auto_earning: 0,
  auto_deduction: 0,
  amount: '',
  status: 0,
  effective_from_date: new Date().toISOString().split('T')[0]
};

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function AddEarningDeductionForm({ setOpenPopup, fetchTableData, onCloseModal, editData }) {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  // Load edit data when editData prop changes
  useEffect(() => {
    if (editData) {
      setValues({
        id: editData.ed_id || 0,
        ed_type: editData.ed_type || '',
        auto_earning: editData.auto_earning || 0,
        auto_deduction: editData.auto_deduction || 0,
        amount: editData.amount || '',
        status: editData.status || 0,
        effective_from_date: editData.effective_from_date ? editData.effective_from_date.split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setValues(initialFValues);
    }
  }, [editData]);

  const validate = () => {
    let temp = {};
    temp.ed_type = values.ed_type ? "" : "This field is required.";
    temp.auto_earning = (values.auto_earning === 1 || values.auto_deduction === 1) ? "" : "Please select either Auto Earning or Auto Deduction.";
    temp.amount = values.amount ? (isNaN(parseFloat(values.amount)) || parseFloat(values.amount) <= 0 ? "Amount must be a positive number." : "") : "This field is required.";
    temp.effective_from_date = values.effective_from_date ? "" : "This field is required.";

    setErrors({
      ...temp
    });

    return Object.values(temp).every(x => x === "");
  };

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  };

  function handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    
    let newValues = { ...values };
    
    if (type === 'checkbox') {
      if (name === 'auto_earning') {
        newValues.auto_earning = checked ? 1 : 0;
        if (checked) {
          newValues.auto_deduction = 0; // Uncheck the other one
          newValues.ed_type = ''; // Reset type of event when switching
        }
      } else if (name === 'auto_deduction') {
        newValues.auto_deduction = checked ? 1 : 0;
        if (checked) {
          newValues.auto_earning = 0; // Uncheck the other one
          newValues.ed_type = ''; // Reset type of event when switching
        }
      } else {
        newValues[name] = checked ? 1 : 0;
      }
    } else {
      newValues[name] = value;
    }
    
    setValues(newValues);
    
    // Clear the specific error for the field being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  }

  // Get dropdown options based on selection
  const getEventTypeOptions = () => {
    if (values.auto_earning === 1) {
      return [
        { value: 'Birthday Gift', label: 'Birthday Gift' },
        { value: 'Marriage Gift', label: 'Marriage Gift' }
      ];
    } else if (values.auto_deduction === 1) {
      return [
        { value: 'Paid Leave', label: 'Paid Leave' },
        { value: 'Unpaid Leave', label: 'Unpaid Leave' }
      ];
    }
    return [];
  };

  // Close modal after successful submission
  useEffect(() => {
    if (notify.isOpen && notify.type === 'success') {
      const timer = setTimeout(() => {
        // Close the modal
        if (setOpenPopup) {
          setOpenPopup(false);
        }
        if (onCloseModal) {
          onCloseModal();
        }
        // Refresh table data
        if (fetchTableData) {
          fetchTableData();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [notify, setOpenPopup, onCloseModal, fetchTableData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const isEditMode = editData && editData.ed_id;
      const API_URL = isEditMode 
        ? `${process.env.REACT_APP_API_BASE_URL}/company-master/edit-auto-earning-deduction`
        : `${process.env.REACT_APP_API_BASE_URL}/company-master/add-auto-earning-deduction`;
      
      // Prepare payload matching the required format
      const payload = {
        ed_type: values.ed_type,
        auto_earning: values.auto_earning,
        auto_deduction: values.auto_deduction,
        amount: parseFloat(values.amount),
        status: values.status,
        effective_from_date: values.effective_from_date
      };

      // Add ed_id for edit mode
      if (isEditMode) {
        payload.ed_id = editData.ed_id;
      }
      
      const apiCall = axios.post(API_URL, payload);
      
      apiCall
        .then(function (response) {
          // Treat both 200 (OK) and 201 (Created) as success
          if (response.status === 200 || response.status === 201) {
            // Reset form
            resetForm();
            // Show success notification - useEffect will handle closing modal and refreshing table
            setNotify({
              isOpen: true,
              message: isEditMode ? 'Successfully updated' : 'Successfully added',
              type: 'success'
            });
          }
        })
        .catch(function (error) {
          if (error.response) {
            if (error.response.status === 401) {
              setNotify({
                isOpen: true,
                message: 'Session Expired!',
                type: 'error'
              });
              setTimeout(() => {
                navigate('/');
              }, 1000);
            } else {
              setNotify({
                isOpen: true,
                message: error.response.data?.error_message || 'An error occurred while saving',
                type: 'error'
              });
            }
          } else {
            setNotify({
              isOpen: true,
              message: 'Network error. Please try again.',
              type: 'error'
            });
          }
        });
    }
  };

  return (
    <Box>
      <form autoComplete="off" onSubmit={handleSubmit}>
        <Stack spacing={3} sx={{ margin: "20px" }}>

          {/* First Row: 2 checkboxes */}
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Checkbox
                  name="auto_earning"
                  color="primary"
                  checked={values.auto_earning === 1}
                  onChange={handleInputChange}
                />
              }
              label="Auto Earning"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="auto_deduction"
                  color="primary"
                  checked={values.auto_deduction === 1}
                  onChange={handleInputChange}
                />
              }
              label="Auto Deduction"
            />
          </Stack>
          {errors.auto_earning && <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>{errors.auto_earning}</Typography>}

          {/* Second Row: Type of Event dropdown */}
          <FormControl
            variant="outlined"
            {...(errors.ed_type && { error: true })}
            fullWidth
            disabled={values.auto_earning === 0 && values.auto_deduction === 0}
          >
            <InputLabel><RequiredLabel label="Type of Event" /></InputLabel>
            <Select
              label={<RequiredLabel label="Type of Event" />}
              name="ed_type"
              size="small"
              value={values.ed_type}
              onChange={handleInputChange}
            >
              {getEventTypeOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.ed_type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.ed_type}</Typography>}
          </FormControl>

          {/* Third Row: 2 fields */}
          <Stack direction="row" spacing={3}>
            <TextField
              variant="outlined"
              label={<RequiredLabel label="Amount" />}
              name="amount"
              type="number"
              size="small"
              value={values.amount}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: 0.01 }}
              {...(errors.amount && { error: true, helperText: errors.amount })}
              fullWidth
            />
            <TextField
              variant="outlined"
              type="date"
              label={<RequiredLabel label="Effective Date" />}
              name="effective_from_date"
              size="small"
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split('T')[0]
                }
              }}
              value={values.effective_from_date}
              onChange={handleInputChange}
              {...(errors.effective_from_date && { error: true, helperText: errors.effective_from_date })}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                name="status"
                color="primary"
                checked={values.status === 1}
                onChange={handleInputChange}
              />
            }
            label="Active?"
            sx={{ mt: 1 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={resetForm}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
            >
              Submit
            </Button>
          </Box>
        </Stack>
      </form>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default AddEarningDeductionForm;

